// frontend/src/utils/exportProduccionDiariaExcel.ts
// Export con formato de la vista Detalle de Producción diaria: una sola hoja
// con un bloque por cada OP (cabecera) → sub-tabla de LOTES + sub-tabla de BOM.
import ExcelJS from 'exceljs'
import type { TFunction } from 'i18next'
import type { ProduccionDiariaRow, ConsumoProduccionRow } from '@/types'

// ── Tipos de agrupación (compartidos con ProduccionDiariaTable) ────────────────

export interface LoteDetalle {
  fecha?: string
  lote?: string
  almacenDestino?: string
  qtyProducida: number
  qtyError: number
  qtyTotalReportada: number
}

export interface GrupoProduccion {
  numOp: string
  articulo: string
  descripcion: string
  familia?: string
  dataareaid: string
  estadoOp?: string
  fechaEntregaOp?: string
  almacenDestino?: string
  qtyPlanificada: number
  qtyProducidaTotal: number
  qtyErrorTotal: number
  lotes: LoteDetalle[]
  distinctLotCount: number
  fechaUltima?: string
  /** Nota del motivo de desviación. Ver `agruparProduccion`. */
  comentarios?: string
}

/** Agrupa filas planas por numOp, preservando el orden de primera aparición.
 *  Dentro de cada grupo ordena lotes por número de lote (natural) y luego fecha. */
export function agruparProduccion(rows: ProduccionDiariaRow[]): GrupoProduccion[] {
  const map = new Map<string, GrupoProduccion>()
  // El comentario (motivo de desviación) viene en la fila de lote, no en una tabla
  // de cabeceras: normalmente es el mismo texto repetido en todas las filas de la
  // OP, pero se acumulan los DISTINTOS por si la nota se anotó a nivel de lote.
  // Así la cabecera nunca pierde una nota ni la muestra duplicada.
  const notas = new Map<string, Set<string>>()
  for (const row of rows) {
    const nota = row.comentarios?.trim()
    if (nota) {
      const set = notas.get(row.numOp) ?? new Set<string>()
      set.add(nota)
      notas.set(row.numOp, set)
    }
    let g = map.get(row.numOp)
    if (!g) {
      g = {
        numOp: row.numOp,
        articulo: row.articulo,
        descripcion: row.descripcion,
        familia: row.familia,
        dataareaid: row.dataareaid,
        estadoOp: row.estadoOp,
        fechaEntregaOp: row.fechaEntregaOp,
        almacenDestino: row.almacenDestino,
        qtyPlanificada: row.qtyPlanificada,
        qtyProducidaTotal: 0,
        qtyErrorTotal: 0,
        lotes: [],
        distinctLotCount: 0,
        fechaUltima: row.fecha,
      }
      map.set(row.numOp, g)
    }
    g.qtyProducidaTotal += row.qtyProducida
    g.qtyErrorTotal += row.qtyError
    if (row.fecha && (!g.fechaUltima || row.fecha > g.fechaUltima)) g.fechaUltima = row.fecha
    g.lotes.push({
      fecha: row.fecha,
      lote: row.lote,
      almacenDestino: row.almacenDestino,
      qtyProducida: row.qtyProducida,
      qtyError: row.qtyError,
      qtyTotalReportada: row.qtyTotalReportada,
    })
  }
  for (const g of map.values()) {
    g.lotes.sort((a, b) => {
      const la = a.lote ?? ''
      const lb = b.lote ?? ''
      if (la !== lb) return la.localeCompare(lb, 'es', { numeric: true })
      return (a.fecha ?? '').localeCompare(b.fecha ?? '')
    })
    g.distinctLotCount = new Set(g.lotes.map(l => l.lote ?? '')).size
    const nota = notas.get(g.numOp)
    if (nota?.size) g.comentarios = [...nota].join('\n')
  }
  return [...map.values()]
}

// ── Paleta (alineada con exportSecuenciaExcel), en ARGB ────────────────────────

const HEADER = { bg: 'FF0284C7', font: 'FFFFFFFF' }   // sky-600
const SECTION = { bg: 'FFE0F2FE', font: 'FF0369A1' }  // sky-100 / sky-700
const BAND = 'FFF8FAFC'                                // slate-50
const BORDER = 'FFE2E8F0'                              // slate-200
const ALERT = { bg: 'FFFEE2E2', font: 'FFB91C1C' }    // red
const NOTE = { bg: 'FFFFF7ED', font: 'FF9A3412' }     // amber-50 / amber-800

const COLS = 8 // nº de columnas de la hoja (máx entre sub-tablas)
/** Caracteres que caben en una línea de la fila combinada (suma de `ws.columns`). */
const NOTE_LINE_CHARS = 118
/** Techo de líneas de la nota: una nota kilométrica no debe empujar el bloque. */
const NOTE_MAX_LINES = 6

function fill(argb: string): ExcelJS.Fill {
  return { type: 'pattern', pattern: 'solid', fgColor: { argb } }
}

function thinBorder() {
  const side = { style: 'thin' as const, color: { argb: BORDER } }
  return { top: side, left: side, bottom: side, right: side }
}

function fmtDate(iso?: string) {
  if (!iso) return ''
  const [y, m, d] = iso.split('-')
  return `${d}/${m}/${y}`
}

/**
 * El estado de la OP, traducido al idioma de la interfaz.
 *
 * El dato llega de D365 en castellano —«Terminado», «Iniciado»— y en pantalla lo
 * traduce `StatusBadge` con esta misma clave. El Excel lo escribía CRUDO, así
 * que un usuario en inglés se descargaba un fichero medio traducido: las
 * cabeceras en inglés y los estados en castellano.
 *
 * `defaultValue` deja pasar intacto un estado que no conozcamos: si D365 estrena
 * uno, es preferible verlo en castellano que ver la clave `production.status.X`.
 */
export function estadoTraducido(t: TFunction, estado?: string): string {
  if (!estado) return ''
  return t(`production.status.${estado}`, { defaultValue: estado })
}

function rendPct(prod: number, plan: number) {
  if (!plan) return null
  return Math.round((prod / plan) * 100)
}

// ── Construcción del workbook (pura, testeable) ────────────────────────────────

export function buildProduccionDiariaWorkbook(
  grupos: GrupoProduccion[],
  bomByOp: Map<string, ConsumoProduccionRow[]>,
  t: TFunction,
  isEmpresaAll: boolean,
  fmtArticulo: (a: string) => string = (a) => a,
): ExcelJS.Workbook {
  const e = (k: string) => t(`production.excel.${k}`)
  const wb = new ExcelJS.Workbook()
  wb.creator = 'Airval Factory'
  const ws = wb.addWorksheet(e('sheetName'))
  ws.columns = [
    { width: 18 }, { width: 34 }, { width: 16 }, { width: 12 },
    { width: 12 }, { width: 12 }, { width: 10 }, { width: 8 },
  ]

  const mergeFull = (rowNumber: number) => ws.mergeCells(rowNumber, 1, rowNumber, COLS)

  const styleSection = (row: ExcelJS.Row) => {
    row.getCell(1).fill = fill(SECTION.bg)
    row.getCell(1).font = { bold: true, color: { argb: SECTION.font }, size: 10 }
  }
  const styleColHeader = (row: ExcelJS.Row, n: number) => {
    for (let c = 1; c <= n; c++) {
      const cell = row.getCell(c)
      cell.font = { bold: true, color: { argb: 'FF64748B' }, size: 10 }
      cell.border = thinBorder()
      cell.fill = fill(BAND)
    }
  }
  const styleDataRow = (row: ExcelJS.Row, n: number, i: number, numFrom: number) => {
    for (let c = 1; c <= n; c++) {
      const cell = row.getCell(c)
      cell.border = thinBorder()
      cell.alignment = { horizontal: c >= numFrom ? 'right' : 'left' }
      if (i % 2 === 1) cell.fill = fill(BAND)
    }
  }

  for (const g of grupos) {
    // ── Título de la OP ──
    const rend = rendPct(g.qtyProducidaTotal, g.qtyPlanificada)
    const titulo = [
      g.numOp, fmtArticulo(g.articulo), g.descripcion, estadoTraducido(t, g.estadoOp),
      `${e('qtyProduced')}: ${g.qtyProducidaTotal}`,
      `${e('qtyPlanned')}: ${g.qtyPlanificada}`,
      rend !== null ? `${e('performance')}: ${rend}%` : '',
      isEmpresaAll ? `${e('company')}: ${g.dataareaid}` : '',
    ].filter(Boolean).join('  ·  ')
    const titRow = ws.addRow([titulo])
    mergeFull(titRow.number)
    titRow.getCell(1).fill = fill(HEADER.bg)
    titRow.getCell(1).font = { bold: true, color: { argb: HEADER.font }, size: 12 }
    titRow.height = 20

    // ── Motivo de desviación ──
    // La nota va en su propia fila bajo el título, no dentro de él: es texto libre
    // y multilínea, y concatenada al resto del título quedaría ilegible. Se omite
    // la fila entera cuando la OP no tiene nota, para no sembrar el informe de
    // filas vacías.
    // Va condicionada a isEmpresaAll por el mismo motivo que la columna de la
    // tabla: si el export la incluyese, el botón de Excel se saltaría la
    // restricción de rol que se acaba de poner en la vista.
    if (isEmpresaAll && g.comentarios) {
      const notaRow = ws.addRow([`${e('deviationReason')}: ${g.comentarios}`])
      mergeFull(notaRow.number)
      const cell = notaRow.getCell(1)
      cell.fill = fill(NOTE.bg)
      cell.font = { italic: true, color: { argb: NOTE.font }, size: 10 }
      cell.alignment = { wrapText: true, vertical: 'top' }
      // Excel NO autoajusta el alto de una celda combinada con wrapText, así que se
      // estima: saltos de línea explícitos más los que provoca el propio ajuste.
      const lineas = g.comentarios
        .split('\n')
        .reduce((n, l) => n + Math.max(1, Math.ceil(l.length / NOTE_LINE_CHARS)), 0)
      notaRow.height = 14 * Math.min(lineas, NOTE_MAX_LINES) + 4
    }

    // ── LOTES ──
    const lotesSec = ws.addRow([e('lotsSection')])
    mergeFull(lotesSec.number)
    styleSection(lotesSec)
    const lotCols = isEmpresaAll
      ? [e('lot'), e('date'), e('warehouse'), e('qtyProduced'), e('lotQtyTotal')]
      : [e('lot'), e('date'), e('qtyProduced'), e('lotQtyTotal')]
    styleColHeader(ws.addRow(lotCols), lotCols.length)
    const lotNumFrom = isEmpresaAll ? 4 : 3 // primera columna numérica
    g.lotes.forEach((l, i) => {
      const vals = isEmpresaAll
        ? [l.lote ?? '', fmtDate(l.fecha), l.almacenDestino ?? '', l.qtyProducida, l.qtyTotalReportada]
        : [l.lote ?? '', fmtDate(l.fecha), l.qtyProducida, l.qtyTotalReportada]
      styleDataRow(ws.addRow(vals), vals.length, i, lotNumFrom)
    })

    // ── BOM / COMPONENTES ──
    const bomSec = ws.addRow([e('bomSection')])
    mergeFull(bomSec.number)
    styleSection(bomSec)
    const bomCols = [
      e('bomComponent'), e('bomDescription'), e('bomFamily'),
      e('bomTheoretical'), e('bomReal'), e('bomDeviation'), e('bomDeviationPct'), e('bomUm'),
    ]
    styleColHeader(ws.addRow(bomCols), bomCols.length)
    const bomLines = bomByOp.get(g.numOp) ?? []
    if (!bomLines.length) {
      const r = ws.addRow([e('noBom')])
      mergeFull(r.number)
      r.getCell(1).font = { italic: true, color: { argb: 'FF94A3B8' } }
    } else {
      bomLines.forEach((c, i) => {
        const vals = [
          c.componente, c.descripcionComponente ?? '', c.familiaComponente ?? '',
          c.qtyTeorica, c.qtyReal, c.desviacion, c.pctDesviacion, c.um ?? '',
        ]
        const r = ws.addRow(vals)
        styleDataRow(r, vals.length, i, 4) // cols 4..8 numéricas/derecha
        r.getCell(7).numFmt = '0.0"%"'     // %Desv como "5.0%"
        if (c.alertaDesviacion === 1) {
          r.getCell(6).fill = fill(ALERT.bg); r.getCell(6).font = { color: { argb: ALERT.font }, bold: true }
          r.getCell(7).fill = fill(ALERT.bg); r.getCell(7).font = { color: { argb: ALERT.font }, bold: true }
        }
      })
    }

    ws.addRow([]) // separador entre OPs
  }

  anadirHojaProducido(wb, grupos, t, isEmpresaAll, fmtArticulo)
  anadirHojaLotes(wb, grupos, t, isEmpresaAll, fmtArticulo)
  anadirHojaBom(wb, grupos, bomByOp, t, isEmpresaAll, fmtArticulo)

  return wb
}

/**
 * Cabecera de una hoja plana: título en sólido, filtro, cabecera congelada.
 *
 * Lo comparten las tres hojas planas (resumen, lotes y BOM) para que no se
 * separen: cuando cada una se peina a mano, la que se toca menos acaba sin
 * filtro o sin congelar y nadie sabe por qué esa no va como las otras.
 */
function peinarHojaPlana(
  ws: ExcelJS.Worksheet,
  cabeceras: string[],
  primeraNum: number,
): void {
  const cabRow = ws.getRow(1)
  cabRow.values = cabeceras
  cabRow.height = 18
  for (let c = 1; c <= cabeceras.length; c++) {
    const cell = cabRow.getCell(c)
    cell.fill = fill(HEADER.bg)
    cell.font = { bold: true, color: { argb: HEADER.font }, size: 10 }
    cell.border = thinBorder()
    cell.alignment = { horizontal: c >= primeraNum ? 'right' : 'left', vertical: 'middle' }
  }
  ws.views = [{ state: 'frozen', ySplit: 1 }]
}

/** Pinta una fila de datos de hoja plana (bandas + bordes + alineación). */
function filaPlana(ws: ExcelJS.Worksheet, vals: unknown[], i: number, primeraNum: number): ExcelJS.Row {
  const r = ws.addRow(vals as ExcelJS.CellValue[])
  for (let c = 1; c <= vals.length; c++) {
    const cell = r.getCell(c)
    cell.border = thinBorder()
    cell.alignment = { horizontal: c >= primeraNum ? 'right' : 'left' }
    if (i % 2 === 1) cell.fill = fill(BAND)
  }
  return r
}

/** Pie con `SUBTOTAL(109, …)` en las columnas indicadas: suma SÓLO lo visible,
 *  así que al filtrar la hoja el total se recalcula solo. Ver `anadirHojaProducido`. */
function pieSubtotales(
  ws: ExcelJS.Worksheet,
  cabeceras: string[],
  columnasSuma: number[],
  primeraNum: number,
  hayDatos: boolean,
  etiquetaTotal: string,
): void {
  const ultima = ws.rowCount
  const vals: (string | { formula: string })[] = new Array(cabeceras.length).fill('')
  vals[0] = etiquetaTotal
  if (hayDatos) {
    for (const c of columnasSuma) {
      vals[c - 1] = { formula: `SUBTOTAL(109,${col(c)}2:${col(c)}${ultima})` }
    }
    ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: ultima, column: cabeceras.length } }
  }
  const totRow = ws.addRow(vals)
  totRow.height = 18
  for (let c = 1; c <= cabeceras.length; c++) {
    const cell = totRow.getCell(c)
    cell.fill = fill(SECTION.bg)
    cell.font = { bold: true, color: { argb: SECTION.font }, size: 11 }
    cell.border = thinBorder()
    cell.alignment = { horizontal: c >= primeraNum ? 'right' : 'left' }
  }
}

/**
 * Tercera hoja: UNA FILA POR LOTE, con los datos de su orden repetidos al lado.
 *
 * La primera hoja ya enseña los lotes, pero metidos en el bloque de su OP: para
 * contestar «¿qué se produjo del lote X?» o «¿cuánto se produjo en marzo?» hay
 * que ir bloque por bloque, porque ni se puede filtrar ni se puede ordenar por
 * lote o por fecha. Aquí sí, porque las columnas de la OP se REPITEN en cada
 * línea: es lo que convierte el informe en una tabla y lo que permite una tabla
 * dinámica por lote, por fecha o por almacén.
 */
function anadirHojaLotes(
  wb: ExcelJS.Workbook,
  grupos: GrupoProduccion[],
  t: TFunction,
  isEmpresaAll: boolean,
  fmtArticulo: (a: string) => string,
): void {
  const e = (k: string) => t(`production.excel.${k}`)
  // 31 caracteres es el techo de Excel para un nombre de hoja, y ExcelJS
  // lo recorta en SILENCIO: mejor que el recorte esté a la vista.
  const ws = wb.addWorksheet(e('lotsSheetName').slice(0, 31))

  const cabeceras = [
    e('opNum'), e('article'), e('description'), e('family'),
    ...(isEmpresaAll ? [e('company')] : []),
    e('opStatus'), e('lot'), e('date'), e('warehouse'),
    e('qtyProduced'), e('lotQtyTotal'),
  ]
  ws.columns = [
    { width: 16 }, { width: 16 }, { width: 38 }, { width: 16 },
    ...(isEmpresaAll ? [{ width: 10 }] : []),
    { width: 16 }, { width: 18 }, { width: 13 }, { width: 12 },
    { width: 14 }, { width: 15 },
  ]

  // Lote, fecha y almacén son texto aunque vayan al final: la primera columna
  // numérica es «producido».
  const PRIMERA_NUM = cabeceras.length - 1
  peinarHojaPlana(ws, cabeceras, PRIMERA_NUM)

  let i = 0
  for (const g of grupos) {
    for (const l of g.lotes) {
      filaPlana(ws, [
        g.numOp, fmtArticulo(g.articulo), g.descripcion, g.familia ?? '',
        ...(isEmpresaAll ? [g.dataareaid] : []),
        estadoTraducido(t, g.estadoOp),
        l.lote ?? '', fmtDate(l.fecha), l.almacenDestino ?? '',
        l.qtyProducida, l.qtyTotalReportada,
      ], i++, PRIMERA_NUM)
    }
  }

  pieSubtotales(
    ws, cabeceras, [cabeceras.length - 1, cabeceras.length],
    PRIMERA_NUM, i > 0, e('total'),
  )
}

/**
 * Cuarta hoja: UNA FILA POR COMPONENTE, con los datos de su orden al lado.
 *
 * Mismo motivo que la de lotes, y además contesta la pregunta que hoy no se
 * puede contestar sin rehacer el fichero: «¿cuánto me he desviado en este
 * componente a lo largo de TODAS las órdenes?». En la hoja de bloques ese
 * componente está repartido entre decenas de sub-tablas.
 *
 * La desviación se marca en rojo con el mismo criterio que la hoja de bloques
 * (`alertaDesviacion`), que viene del backend y no se recalcula aquí: dos
 * umbrales en dos sitios es un sitio donde discrepar.
 */
function anadirHojaBom(
  wb: ExcelJS.Workbook,
  grupos: GrupoProduccion[],
  bomByOp: Map<string, ConsumoProduccionRow[]>,
  t: TFunction,
  isEmpresaAll: boolean,
  fmtArticulo: (a: string) => string,
): void {
  const e = (k: string) => t(`production.excel.${k}`)
  // 31 caracteres es el techo de Excel para un nombre de hoja, y ExcelJS
  // lo recorta en SILENCIO: mejor que el recorte esté a la vista.
  const ws = wb.addWorksheet(e('bomSheetName').slice(0, 31))

  const cabeceras = [
    e('opNum'), e('article'), e('description'),
    ...(isEmpresaAll ? [e('company')] : []),
    e('opStatus'), e('lastDate'),
    e('bomComponent'), e('bomDescription'), e('bomFamily'), e('bomUm'),
    e('bomTheoretical'), e('bomReal'), e('bomDeviation'), e('bomDeviationPct'),
  ]
  ws.columns = [
    { width: 16 }, { width: 16 }, { width: 34 },
    ...(isEmpresaAll ? [{ width: 10 }] : []),
    { width: 16 }, { width: 13 },
    { width: 18 }, { width: 34 }, { width: 16 }, { width: 8 },
    { width: 14 }, { width: 14 }, { width: 14 }, { width: 14 },
  ]

  const PRIMERA_NUM = cabeceras.length - 3   // teórica, real, desviación, %
  const iPct = cabeceras.length
  const iDesv = cabeceras.length - 1
  peinarHojaPlana(ws, cabeceras, PRIMERA_NUM)

  let i = 0
  for (const g of grupos) {
    for (const c of bomByOp.get(g.numOp) ?? []) {
      const r = filaPlana(ws, [
        g.numOp, fmtArticulo(g.articulo), g.descripcion,
        ...(isEmpresaAll ? [g.dataareaid] : []),
        estadoTraducido(t, g.estadoOp), fmtDate(g.fechaUltima),
        c.componente, c.descripcionComponente ?? '', c.familiaComponente ?? '', c.um ?? '',
        c.qtyTeorica, c.qtyReal, c.desviacion, c.pctDesviacion,
      ], i++, PRIMERA_NUM)
      r.getCell(iPct).numFmt = '0.0"%"'
      if (c.alertaDesviacion === 1) {
        for (const n of [iDesv, iPct]) {
          r.getCell(n).fill = fill(ALERT.bg)
          r.getCell(n).font = { color: { argb: ALERT.font }, bold: true }
        }
      }
    }
  }

  // El % NO se suma: es un ratio. Teórica, real y desviación sí.
  pieSubtotales(
    ws, cabeceras, [PRIMERA_NUM, PRIMERA_NUM + 1, iDesv],
    PRIMERA_NUM, i > 0, e('total'),
  )
}

/** Letra de columna de Excel a partir de su índice (1 → A). */
function col(n: number): string {
  let s = ''
  while (n > 0) { const r = (n - 1) % 26; s = String.fromCharCode(65 + r) + s; n = Math.floor((n - 1) / 26) }
  return s
}

/**
 * Segunda hoja: UNA FILA POR ORDEN, plana, con el total al pie.
 *
 * Existe porque la primera hoja es un informe para LEER —un bloque por orden,
 * con sus lotes y su escandallo debajo— y eso no se puede sumar: las cantidades
 * están repartidas entre bloques y a distintas alturas, así que hacer un total
 * o una tabla dinámica obliga a rehacer el fichero a mano.
 *
 * Aquí cada orden ocupa una línea y las columnas son siempre las mismas, que es
 * lo que Excel necesita para filtrar, ordenar y sumar.
 */
function anadirHojaProducido(
  wb: ExcelJS.Workbook,
  grupos: GrupoProduccion[],
  t: TFunction,
  isEmpresaAll: boolean,
  fmtArticulo: (a: string) => string,
): void {
  const e = (k: string) => t(`production.excel.${k}`)
  const ws = wb.addWorksheet(e('summarySheetName'))

  const cabeceras = [
    e('opNum'), e('article'), e('description'), e('family'),
    ...(isEmpresaAll ? [e('company')] : []),
    e('opStatus'), e('lastDate'), e('lotCount'),
    e('qtyPlanned'), e('qtyProduced'), e('qtyPending'), e('performance'),
  ]
  ws.columns = [
    { width: 16 }, { width: 16 }, { width: 38 }, { width: 16 },
    ...(isEmpresaAll ? [{ width: 10 }] : []),
    { width: 16 }, { width: 13 }, { width: 9 },
    { width: 13 }, { width: 13 }, { width: 13 }, { width: 13 },
  ]

  // Primera columna numérica: de aquí a la derecha todo se alinea a la derecha y
  // entra en los totales.
  const PRIMERA_NUM = isEmpresaAll ? 8 : 7
  const iLotes = PRIMERA_NUM
  const iPlan = PRIMERA_NUM + 1
  const iProd = PRIMERA_NUM + 2
  const iPend = PRIMERA_NUM + 3
  const iRend = PRIMERA_NUM + 4

  const cabRow = ws.addRow(cabeceras)
  cabRow.height = 18
  for (let c = 1; c <= cabeceras.length; c++) {
    const cell = cabRow.getCell(c)
    cell.fill = fill(HEADER.bg)
    cell.font = { bold: true, color: { argb: HEADER.font }, size: 10 }
    cell.border = thinBorder()
    cell.alignment = { horizontal: c >= PRIMERA_NUM ? 'right' : 'left', vertical: 'middle' }
  }

  grupos.forEach((g, i) => {
    const vals = [
      g.numOp, fmtArticulo(g.articulo), g.descripcion, g.familia ?? '',
      ...(isEmpresaAll ? [g.dataareaid] : []),
      estadoTraducido(t, g.estadoOp), fmtDate(g.fechaUltima), g.distinctLotCount,
      g.qtyPlanificada, g.qtyProducidaTotal,
      // Lo que queda por producir. Ocupa el sitio de la cantidad de error, que
      // en este dato viene SIEMPRE vacía: una columna de ceros no informa de
      // nada y encima invita a sumarla.
      g.qtyPlanificada - g.qtyProducidaTotal,
      // El rendimiento va como FRACCIÓN con formato de porcentaje, no como el
      // número 87: así Excel lo trata como número y se puede ordenar y promediar.
      g.qtyPlanificada ? g.qtyProducidaTotal / g.qtyPlanificada : null,
    ]
    const r = ws.addRow(vals)
    for (let c = 1; c <= vals.length; c++) {
      const cell = r.getCell(c)
      cell.border = thinBorder()
      cell.alignment = { horizontal: c >= PRIMERA_NUM ? 'right' : 'left' }
      if (i % 2 === 1) cell.fill = fill(BAND)
    }
    // '0%' —y no '0"%"'— porque el valor es una FRACCIÓN: el primero la
    // multiplica por cien y escribe «83%», el segundo pega el signo como texto
    // y deja «1%», que fue exactamente el fallo.
    r.getCell(iRend).numFmt = '0%'
    // Lo producido de más se marca: no es un error, pero se mira distinto.
    if (g.qtyPlanificada - g.qtyProducidaTotal < 0) {
      r.getCell(iPend).font = { color: { argb: ALERT.font }, bold: true }
    }
  })

  const primera = 2
  const ultima = ws.rowCount

  // ── Total al pie ──
  //
  // Va como FÓRMULA `SUBTOTAL(109, …)` y no como un número ya calculado, y esa
  // es la diferencia que resuelve la queja: 109 suma SÓLO las filas visibles, así
  // que al filtrar por estado —o por familia, o por empresa— el total de abajo se
  // recalcula solo. Un número fijo obligaría a rehacer la suma a mano cada vez,
  // que es justo lo que se venía haciendo.
  const rango = (c: number) => `${col(c)}${primera}:${col(c)}${ultima}`
  const totalVals: (string | { formula: string })[] = new Array(cabeceras.length).fill('')
  totalVals[0] = e('total')
  if (grupos.length > 0) {
    for (const c of [iLotes, iPlan, iProd, iPend]) {
      totalVals[c - 1] = { formula: `SUBTOTAL(109,${rango(c)})` }
    }
    // El rendimiento total NO es la suma ni la media de los rendimientos de cada
    // orden: es el total producido entre el total planificado. Promediar
    // porcentajes daría el mismo peso a una orden de 10 unidades que a una de
    // 10.000.
    totalVals[iRend - 1] = {
      formula: `IF(SUBTOTAL(109,${rango(iPlan)})=0,"",SUBTOTAL(109,${rango(iProd)})/SUBTOTAL(109,${rango(iPlan)}))`,
    }
  }
  const totRow = ws.addRow(totalVals)
  totRow.height = 18
  for (let c = 1; c <= cabeceras.length; c++) {
    const cell = totRow.getCell(c)
    cell.fill = fill(SECTION.bg)
    cell.font = { bold: true, color: { argb: SECTION.font }, size: 11 }
    cell.border = thinBorder()
    cell.alignment = { horizontal: c >= PRIMERA_NUM ? 'right' : 'left' }
  }
  totRow.getCell(iRend).numFmt = '0%'

  // Filtro en la cabecera y cabecera congelada: sin esto, filtrar obliga a
  // seleccionar el rango a mano y al bajar se pierde de vista qué es cada columna.
  // El filtro NO incluye la fila de total, o Excel la trataría como un dato más.
  if (grupos.length > 0) {
    ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: ultima, column: cabeceras.length } }
  }
  ws.views = [{ state: 'frozen', ySplit: 1 }]
}

/** Construye y descarga el Excel de la vista Detalle. */
export async function exportProduccionDiariaExcel(
  grupos: GrupoProduccion[],
  bomByOp: Map<string, ConsumoProduccionRow[]>,
  t: TFunction,
  filenameBase: string,
  isEmpresaAll: boolean,
  fmtArticulo: (a: string) => string = (a) => a,
): Promise<void> {
  const wb = buildProduccionDiariaWorkbook(grupos, bomByOp, t, isEmpresaAll, fmtArticulo)
  const buf = await wb.xlsx.writeBuffer()
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${filenameBase}.xlsx`
  a.click()
  URL.revokeObjectURL(url)
}
